import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  approveSessionSummary,
  saveSessionSummary,
  shareSessionSummary,
  unshareSessionSummary,
} from '@/api/sessionSummaries/sessionSummariesApi';
import type {
  SessionSummary,
  SessionSummaryWritePayload,
} from '@/api/sessionSummaries/sessionSummariesApi.types';
import { buildSessionSummaryQueryKey } from '@/constants/queryKeys';

function useSessionSummaryInvalidation() {
  const queryClient = useQueryClient();
  return function invalidate(summary: SessionSummary) {
    queryClient.invalidateQueries({
      queryKey: buildSessionSummaryQueryKey(summary.sessionNoteId),
    });
  };
}

interface SaveSessionSummaryVariables {
  noteId: string;
  payload: SessionSummaryWritePayload;
}

export function useSaveSessionSummaryMutation(): UseMutationResult<
  SessionSummary,
  Error,
  SaveSessionSummaryVariables
> {
  const invalidate = useSessionSummaryInvalidation();
  return useMutation({
    mutationFn: function save(variables: SaveSessionSummaryVariables) {
      return saveSessionSummary(variables.noteId, variables.payload);
    },
    onSuccess: invalidate,
  });
}

export function useApproveSessionSummaryMutation(): UseMutationResult<
  SessionSummary,
  Error,
  string
> {
  const invalidate = useSessionSummaryInvalidation();
  return useMutation({ mutationFn: approveSessionSummary, onSuccess: invalidate });
}

export function useShareSessionSummaryMutation(): UseMutationResult<
  SessionSummary,
  Error,
  string
> {
  const invalidate = useSessionSummaryInvalidation();
  return useMutation({ mutationFn: shareSessionSummary, onSuccess: invalidate });
}

export function useUnshareSessionSummaryMutation(): UseMutationResult<
  SessionSummary,
  Error,
  string
> {
  const invalidate = useSessionSummaryInvalidation();
  return useMutation({ mutationFn: unshareSessionSummary, onSuccess: invalidate });
}
